import { Checkbox, InputLabel, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useContext, useMemo } from 'react';
import WarningPopup from '../../../Popups/WarningPopup';
import { useTripRateContext } from '../../TripRateContext';
import { SessionContext } from '../../../Contexts/SessionContext';
import { CounterBadge } from '../../../Utilities/CounterBadge';

export default function ParkingSpacesRange() {
    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);

    const minMaxParam = useMemo(() => {
        if (!Array.isArray(surveys) || surveys.length === 0) return null;

        const validValues = surveys
            .map(s => s.parkingTotal)
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[];

        if (validValues.length === 0) return null;

        const min = Math.min(...validValues);
        const max = Math.max(...validValues);
        const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length;

        setSessionField('primaryFilters.minParkingSpaces', min);
        setSessionField('primaryFilters.maxParkingSpaces', max);

        return { min, max, avg };
    }, [surveys]);

    const parkingSpacesChecked =
        sessionState?.primaryFilters?.parkingSpacesChecked ?? true;

    const min = sessionState?.primaryFilters?.minParkingSpaces ?? '';
    const max = sessionState?.primaryFilters?.maxParkingSpaces ?? '';

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSessionField('primaryFilters.parkingSpacesChecked', e.target.checked);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const parsed = value === '' ? '' : Number(value);

        if (name === 'minParkingSpaces') {
            setSessionField('primaryFilters.minParkingSpaces', parsed);
        } else if (name === 'maxParkingSpaces') {
            setSessionField('primaryFilters.maxParkingSpaces', parsed);
        }
    };

    return (
        <Grid container component={Paper} size={12} alignItems="center" sx={{ position: 'relative' }}>

            <CounterBadge value={4} />
            <Grid size={12}>
                <Typography textAlign='center' variant="h6">Parking Spaces Range <WarningPopup
                        title="Warning on Parking Spaces"
                        content="By default, all surveys are selected. However, be aware that some sites have unknown numbers of parking spaces, and if users activate the parking spaces selection by unticking the box, then all sites with unknown levels of parking will be excluded from the selected list."
                        type="warning"
                    /></Typography>
            </Grid>

            <Grid size={6}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <InputLabel id="parkingSpacesLabel">All Sites Selected</InputLabel>
                    <Checkbox
                        name="parkingSpacesChecked"
                        checked={parkingSpacesChecked}
                        onChange={handleCheckboxChange}
                    />
                </Stack>
            </Grid>

            <Grid container size={12}>
                <Grid size={4}>
                    <Stack spacing={3}>
                        <Typography variant="body1">
                            Minimum: {minMaxParam?.min ?? '�'}
                        </Typography>
                        <Typography variant="body1">
                            Maximum: {minMaxParam?.max ?? '�'}
                        </Typography>
                    </Stack>
                </Grid>

                {!parkingSpacesChecked && (
                    <Grid size={8}>
                        <Stack spacing={1}>
                            <Grid size={12}>
                                <TextField
                                    name="minParkingSpaces"
                                    value={min}
                                    onChange={handleInputChange}
                                    label="From:"
                                    type="number"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    name="maxParkingSpaces"
                                    value={max}
                                    onChange={handleInputChange}
                                    label="To:"
                                    type="number"
                                />
                            </Grid>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}
